import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
    baseURL: API,
    withCredentials: true,
});

// === Upsell nudges ====================================================
// When the backend rejects a request because the user hit a free-tier
// limit, the response carries a structured `detail.code` we recognise.
// We surface a contextual toast with a CTA → /billing. Throttled so a
// retry-loop doesn't spam the user with duplicate toasts.
const UPSELL_TOASTS = {
    ai_rate_limit: {
        title: "Hit your free AI limit",
        cta: "Get 6× more AI →",
    },
    upload_too_large: {
        title: "Free uploads cap at 4MB",
        cta: "Upgrade to 10MB →",
    },
    pro_required: {
        title: "Pro required",
        cta: "See Pro perks →",
    },
};
let _lastUpsellAt = 0;

function maybeShowUpsell(detail) {
    if (!detail || typeof detail !== "object") return false;
    const code = detail.code;
    if (!code || !UPSELL_TOASTS[code]) return false;
    // Backend marks the error `pro_eligible: false` when the caller is already Pro
    // (and the limit was the Pro/admin one — no upsell to offer).
    if (detail.pro_eligible === false) return false;
    // Throttle: at most one upsell toast every 4s across the whole app.
    const now = Date.now();
    if (now - _lastUpsellAt < 4000) return true;
    _lastUpsellAt = now;
    const { title, cta } = UPSELL_TOASTS[code];
    toast(title, {
        description: detail.message || undefined,
        duration: 7000,
        action: { label: cta, onClick: () => { window.location.href = "/billing"; } },
    });
    return true;
}

api.interceptors.response.use(
    (r) => r,
    (err) => {
        try { maybeShowUpsell(err?.response?.data?.detail); } catch (_e) { /* never break the rejection chain */ }
        return Promise.reject(err);
    },
);

export function formatErr(err) {
    const d = err?.response?.data?.detail;
    if (!d) return err?.message || "Something went wrong";
    if (typeof d === "string") return d;
    // Structured upsell payloads expose a `message` field.
    if (typeof d === "object" && typeof d.message === "string") return d.message;
    if (Array.isArray(d)) return d.map((e) => e?.msg || JSON.stringify(e)).join(" ");
    return String(d);
}

export function timeAgo(iso) {
    if (!iso) return "";
    const t = new Date(iso).getTime();
    const diff = (Date.now() - t) / 1000;
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
    if (diff < 2592000) return `${Math.floor(diff / 86400)}d`;
    return new Date(iso).toLocaleDateString();
}
